import {
    DeletePageCommentArgs,
    AddPageCommentArgs,
    EditPageCommentArgs,
    GetPageCommentsArgs,
    GetThreadCommentsArgs,
} from '~shared/schema/wiki/actions/comments';

import {PageComment} from '~shared/models/wiki';

import {createThunk, makeThunkErrorHandler} from '~ui/store/utils';
import {i18n} from '~ui/components/CommentsPanel/i18n';

const PAGE_SIZE = 50;

export const getComments = createThunk(
    'comments/get',
    async ({pageId, cursor}: GetPageCommentsArgs, {extra: {sdk}}) => {
        try {
            let results: PageComment[] = [];
            let comments = await sdk.wiki.getPageComments({
                pageId,
                pageSize: PAGE_SIZE,
                cursor,
            });

            while (comments.next_cursor) {
                results = results.concat(comments.results);
                comments = await sdk.wiki.getPageComments({
                    pageId,
                    pageSize: PAGE_SIZE,
                    cursor: comments.next_cursor,
                });
            }

            return {
                pageId,
                comments: {
                    next_cursor: comments.next_cursor,
                    prev_cursor: comments.prev_cursor,
                    results: results.concat(comments.results),
                },
            };
        } catch (e) {
            return makeThunkErrorHandler({
                name: 'get-comments',
                title: i18n('error-get-title'),
            })(e);
        }
    },
);

export const getThreadComments = createThunk(
    'comments/get-thread-comments',
    async ({pageId, cursor, commentId}: GetThreadCommentsArgs, {extra: {sdk}}) => {
        try {
            let results: PageComment[] = [];
            let comments = await sdk.wiki.getThreadComments({
                pageId,
                pageSize: PAGE_SIZE,
                commentId,
                cursor,
            });

            while (comments.next_cursor) {
                results = results.concat(comments.results);
                comments = await sdk.wiki.getThreadComments({
                    pageId,
                    pageSize: PAGE_SIZE,
                    commentId,
                    cursor: comments.next_cursor,
                });
            }

            return {
                pageId,
                commentId,
                comments: {
                    next_cursor: comments.next_cursor,
                    prev_cursor: comments.prev_cursor,
                    results: results.concat(comments.results),
                },
            };
        } catch (e) {
            return makeThunkErrorHandler({
                name: 'get-comments',
                title: i18n('error-get-title'),
            })(e);
        }
    },
);

export const deleteComment = createThunk(
    'comments/delete',
    async ({pageId, commentId}: DeletePageCommentArgs, {extra: {sdk}}) => {
        return sdk.wiki
            .deletePageComment({pageId, commentId})
            .then(() => {
                return {
                    pageId,
                    comment: {id: commentId},
                };
            })
            .catch(
                makeThunkErrorHandler({
                    name: 'delete-comment',
                    title: i18n('error-delete-title'),
                }),
            );
    },
);

export const addComment = createThunk(
    'comments/add',
    async (args: AddPageCommentArgs, {extra: {sdk}}) => {
        return sdk.wiki
            .addPageComment(args)
            .then((comment) => {
                return {
                    pageId: args.pageId,
                    comment,
                };
            })
            .catch(
                makeThunkErrorHandler({
                    name: 'add-comment',
                    title: i18n('error-add-title'),
                }),
            );
    },
);

export const editComment = createThunk(
    'comments/edit',
    async ({pageId, body, commentId}: EditPageCommentArgs, {extra: {sdk}}) => {
        return sdk.wiki.editPageComment({pageId, body, commentId}).catch(
            makeThunkErrorHandler({
                name: 'edit-comment',
                title: i18n('error-edit-title'),
            }),
        );
    },
);
